import React, { FunctionComponent, useRef } from "react";
import { useField } from "react-final-form";
import { graphql } from "react-relay";

import { USER_BADGE_OPTIONS } from "coral-admin/constants/badges";
import { QueryRenderData, QueryRenderer } from "coral-framework/lib/relay";
import {
  CheckBox,
  Flex,
  FormField,
  Option,
  SelectField,
} from "coral-ui/components/v2";
import { QueryError } from "coral-ui/components/v3";

import { SiteRoleModalSelectedSiteQuery as QueryTypes } from "coral-admin/__generated__/SiteRoleModalSelectedSiteQuery.graphql";

import styles from "./SiteRoleModalSelectedSiteQuery.css";

interface Props {
  siteID: string;
  onChange: (id: string | null, checked: boolean) => void;
  checked: boolean;
  showBadgeDropdown?: boolean;
  siteUsernameName: string | null;
  submitting?: boolean;
}

const SiteRoleModalSelectedSiteQuery: FunctionComponent<Props> = ({
  siteID,
  onChange,
  checked,
  showBadgeDropdown = false,
  siteUsernameName,
  submitting = false,
}) => {
  const { input: siteBadgesInput } = useField<Record<string, string>>(
    "siteBadges"
  );

  const badgeRef = useRef<string | null>(null);

  const currentValue = siteBadgesInput.value?.[siteID] || "";

  if (submitting && badgeRef.current === null) {
    badgeRef.current = currentValue;
  } else if (!submitting) {
    badgeRef.current = null;
  }

  const displayValue = submitting ? badgeRef.current || "" : currentValue;

  const handleBadgeChange = (badgeName: string) => {
    const updated = { ...siteBadgesInput.value };
    updated[siteID] = badgeName;
    siteBadgesInput.onChange(updated);
  };

  return (
    <QueryRenderer<QueryTypes>
      query={graphql`
        query SiteRoleModalSelectedSiteQuery($siteID: ID!) {
          site(id: $siteID) {
            name
          }
        }
      `}
      variables={{
        siteID,
      }}
      render={({ error, props }: QueryRenderData<QueryTypes>) => {
        if (error) {
          return <QueryError error={error} />;
        }
        if (props && props.site) {
          return (
            <div className={styles.siteRow}>
              <Flex direction="column" itemGutter="half">
                <CheckBox
                  key={siteID}
                  checked={checked}
                  onChange={() => onChange(siteID, checked)}
                >
                  {props.site.name}
                  {siteUsernameName ? (
                    <span className={styles.siteUsername}>
                      ({siteUsernameName})
                    </span>
                  ) : (
                    <span className={styles.userNotExist}>
                      (User doesn't exist on this site)
                    </span>
                  )}
                </CheckBox>
                {showBadgeDropdown && checked && (
                  <FormField>
                    <SelectField
                      id={`badge-${siteID}`}
                      value={displayValue}
                      onChange={(e) => handleBadgeChange(e.target.value)}
                      fullWidth
                      disabled={!siteUsernameName || submitting}
                    >
                      {USER_BADGE_OPTIONS.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </SelectField>
                  </FormField>
                )}
              </Flex>
            </div>
          );
        } else {
          return null;
        }
      }}
    />
  );
};

export default SiteRoleModalSelectedSiteQuery;
